import type {
  CompositionV3Ent,
  CompositionV4Corrections,
  CompositionV4Req,
} from '@revelio/data-access';

import {
  selectionIdToWorkforceSegment,
  workforceSegmentToWorkforceType,
} from '../../../../shared/user-preferences/utils/workforce-segment';
import {
  type Filter,
  FilterId,
  FilterType,
  type PrimaryFilterId,
} from '../../filters/types/filters';
import { getCompositionV3Filters } from '../../filters/utils/composition-v3/getCompositionV3Filters';
import { getV3Entities } from '../../filters/utils/get-v3-entities';
import { getV3Taxonomies } from '../../filters/utils/get-v3-taxonomies';
import { stripEntitiesFromFilters } from '../../filters/utils/role-skill-ent-kind';
import type { PlannedQuery } from './plan-data-queries';

export const isSnapshotQuery = (tables: PlannedQuery['tables']): boolean =>
  Object.values(tables).every(
    ({ dimensions }) => !dimensions.includes('timeseries')
  );

export const buildCompositionV4Request = ({
  dimension,
  filters,
  entities = getV3Entities({ primaryDimension: dimension, filters }),
  isSnapshot = false,
}: {
  dimension: PrimaryFilterId;
  filters: Filter[];
  entities?: CompositionV3Ent[];
  isSnapshot?: boolean;
}): CompositionV4Req => {
  const compositionV3Filters = stripEntitiesFromFilters(
    getCompositionV3Filters({
      primaryDimension: dimension,
      isSnapshot,
      effectiveFilters: filters,
    }),
    entities
  );

  return {
    entities,
    filters: compositionV3Filters,
    corrections: getCompositionV4Corrections(filters),
    ...getV4Taxonomies(filters),
  };
};

export const isCompositionV4RequestComplete = ({
  entities,
  filters,
}: CompositionV4Req): boolean =>
  entities.length > 0 && Boolean(filters?.end_date);

const selectedId = (filters: Filter[], filterId: FilterId) => {
  const filter = filters.find(({ id }) => id === filterId);
  return filter?.type === FilterType.STATIC_LIST
    ? filter.selections[0]?.id
    : undefined;
};

const getCompositionV4Corrections = (
  effectiveFilters: Filter[]
): CompositionV4Corrections => ({
  with_weight:
    selectedId(effectiveFilters, FilterId.DATA_ADJUSTMENTS) !== 'true',
  with_sample_weight: true,
  workforce_type: workforceSegmentToWorkforceType(
    selectionIdToWorkforceSegment(
      selectedId(effectiveFilters, FilterId.CONTINGENT_WORKERS)
    )
  ),
});

type CompositionV4ReqTaxonomies = Pick<
  CompositionV4Req,
  'activity_taxonomy' | 'role_taxonomy' | 'skill_taxonomy'
>;

const getV4Taxonomies = (filters: Filter[]): CompositionV4ReqTaxonomies =>
  getV3Taxonomies(filters);
